import React, { useState } from "react";
import { useIntl } from "react-intl";
import {
  Box,
  Stack,
  TextField,
  Button,
  FormControlLabel,
  Checkbox,
  CircularProgress,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import { VehicleDto, VehicleUpdateRequest } from "../../generated/api-client";
import { AppClient as ClientService } from "../../services/client.service";
import { DropdownOdata } from "../DropdownOdata";
import { Center } from "../Center";
import { useToast } from "../ToastContext";

interface VehicleEditFormProps {
  vehicle: VehicleDto;
  onSaveSuccess?: (updatedVehicle: VehicleDto) => void;
  onCancel?: () => void;
}

export function VehicleEditForm({
  vehicle,
  onSaveSuccess,
  onCancel,
}: VehicleEditFormProps) {
  const intl = useIntl();
  const { showToast } = useToast();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [vehicleModelError, setVehicleModelError] = useState<string | null>(
    null
  );
  const [formData, setFormData] = useState<VehicleUpdateRequest>(() => {
    const updateRequest = new VehicleUpdateRequest();
    updateRequest.name = vehicle.name;
    updateRequest.serialNumber = vehicle.serialNumber || "";
    updateRequest.modelId = vehicle.model?.id || "";
    updateRequest.hasTrackerIoT = vehicle.hasTrackerIoT;
    return updateRequest;
  });

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const updatedVehicle = await ClientService.getInstance().vehiclesPUT(
        vehicle.id,
        ClientService.version,
        formData
      );
      showToast(
        intl.formatMessage({ id: "vehicleSavedSuccessfully" }),
        "success"
      );
      onSaveSuccess?.(updatedVehicle);
    } catch (err: any) {
      setError(
        err.message || intl.formatMessage({ id: "failedToSaveVehicle" })
      );
    } finally {
      setSaving(false);
    }
  };

  if (saving) {
    return (
      <Box sx={{ my: "5rem" }}>
        <Center>
          <CircularProgress size={60} />
        </Center>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        p: 2,
        bgcolor: "background.paper",
        borderRadius: 1,
        mb: 2,
      }}
    >
      <Stack spacing={2}>
        {error && (
          <Box sx={{ color: "error.main", fontSize: "0.875rem" }}>{error}</Box>
        )}
        <TextField
          label={intl.formatMessage({ id: "serialNumber" })}
          value={formData.serialNumber || ""}
          onChange={(e) => {
            const updated = new VehicleUpdateRequest(formData);
            updated.serialNumber = e.target.value;
            setFormData(updated);
          }}
          fullWidth
          required
        />
        <DropdownOdata
          url="vehicle-models"
          fieldNameId="id"
          fieldNameDescription="name"
          value={formData.modelId || null}
          onChange={(newValue) => {
            const updated = new VehicleUpdateRequest(formData);
            updated.modelId = newValue || "";
            setFormData(updated);
            setVehicleModelError(null);
          }}
          error={vehicleModelError}
          label="model"
          initSelected={vehicle.model}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={formData.hasTrackerIoT}
              onChange={(e) => {
                const updated = new VehicleUpdateRequest(formData);
                updated.hasTrackerIoT = e.target.checked;
                setFormData(updated);
              }}
            />
          }
          label={intl.formatMessage({ id: "hasTrackerIoT" })}
        />
        <Box sx={{ display: "flex", justifyContent: "right", gap: 1 }}>
          {onCancel && (
            <Button variant="outlined" onClick={onCancel} disabled={saving}>
              {intl.formatMessage({ id: "deleteCancelLabel" })}
            </Button>
          )}
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={saving}
          >
            {intl.formatMessage({ id: "save" })}
          </Button>
        </Box>
      </Stack>
    </Box>
  );
}
